/* Centralized logging utility
 * Features:
 * - Named logger instances (namespaces): const log = createLogger('clients');
 * - Log levels: debug < info < warn < error
 * - Global enable/disable + per-namespace filtering (glob-like: app:*, clients, feature:auth)
 * - Persist user preference in localStorage (key: __app_log_cfg)
 * - Environment-based defaults: in dev all levels on; in prod only warn+error unless explicitly enabled
 * - Lightweight (no external deps) and tree-shakeable (dead code elimination for DEBUG statements when using vite define).
 */

export type LogLevelName = "debug" | "info" | "warn" | "error" | "silent";
export type InternalLevel = 0 | 1 | 2 | 3 | 4; // maps to above order

const LEVELS: Record<Exclude<LogLevelName, "silent">, InternalLevel> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

interface StoredConfig {
  level: LogLevelName; // global minimum
  filters: string[]; // enabled namespace patterns (empty => all by level)
  disabled?: string[]; // explicitly disabled namespace patterns
}

const STORAGE_KEY = "__app_log_cfg";
const isBrowser = typeof window !== "undefined";
const env = ((import.meta as unknown as { env?: Record<string, unknown> })
  .env ?? {}) as Record<string, unknown>;
const isDev = Boolean(env.DEV);

// Default config differs by environment
// Allow build-time env overrides
const envLevel = (
  env.VITE_LOG_LEVEL as LogLevelName | undefined
)?.toLowerCase() as LogLevelName | undefined;
const envFilters = (env.VITE_LOG_NAMESPACES as string | undefined)?.trim();

const defaultConfig: StoredConfig = {
  level:
    envLevel && ["debug", "info", "warn", "error", "silent"].includes(envLevel)
      ? envLevel
      : isDev
        ? "debug"
        : "warn",
  filters: envFilters ? envFilters.split(/[\s,]+/).filter(Boolean) : [],
  disabled: [],
};

let currentConfig: StoredConfig = loadStoredConfig() ?? defaultConfig;

// Query param overrides (?log=store:* ,api:*&loglvl=debug)
if (isBrowser) {
  try {
    const usp = new URLSearchParams(window.location.search);
    const qpLevel = usp.get("loglvl") as LogLevelName | null;
    const qpFilters = usp.get("log");
    if (
      qpLevel &&
      ["debug", "info", "warn", "error", "silent"].includes(qpLevel)
    ) {
      currentConfig.level = qpLevel;
    }
    if (qpFilters) {
      currentConfig.filters = qpFilters.split(/[\s,]+/).filter(Boolean);
    }
  } catch {
    /* ignore */
  }
}

function loadStoredConfig(): StoredConfig | null {
  if (!isBrowser) return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConfig;
    return { ...defaultConfig, ...parsed };
  } catch {
    return null;
  }
}

function persistConfig() {
  if (!isBrowser) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(currentConfig));
  } catch {
    /* ignore */
  }
}

function levelValue(name: LogLevelName): InternalLevel {
  if (name === "silent") return 4;
  return LEVELS[name];
}

function matchesPattern(name: string, pattern: string): boolean {
  if (pattern === "*") return true;
  // simple glob: * wildcard only
  if (!pattern.includes("*")) return name === pattern;
  const escaped = pattern.replace(/[-/\\^$+?.()|[\]{}]/g, (r) => `\\${r}`);
  const regex = new RegExp("^" + escaped.replace(/\*/g, ".*") + "$");
  return regex.test(name);
}

function namespaceEnabled(ns: string): boolean {
  const { filters, disabled } = currentConfig;
  if (disabled && disabled.some((p) => matchesPattern(ns, p))) return false;
  if (!filters || filters.length === 0) return true; // no allow list -> all allowed
  return filters.some((p) => matchesPattern(ns, p));
}

export interface Logger {
  ns: string;
  setLevel(level: LogLevelName): void; // overrides global? (we keep global only for simplicity now)
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
}

export function configureLogger(partial: Partial<StoredConfig>) {
  currentConfig = { ...currentConfig, ...partial };
  persistConfig();
}

export function getLoggerConfig(): StoredConfig {
  return { ...currentConfig };
}

export function enableNamespaces(patterns: string) {
  const list = patterns.split(/[,\s]+/).filter(Boolean);
  currentConfig.filters = Array.from(
    new Set([...(currentConfig.filters || []), ...list])
  );
  persistConfig();
}

export function disableNamespaces(patterns: string) {
  const list = patterns.split(/[,\s]+/).filter(Boolean);
  currentConfig.disabled = Array.from(
    new Set([...(currentConfig.disabled || []), ...list])
  );
  persistConfig();
}

export function clearFilters() {
  currentConfig.filters = [];
  currentConfig.disabled = [];
  persistConfig();
}

export function setGlobalLevel(level: LogLevelName) {
  currentConfig.level = level;
  persistConfig();
}

function shouldPrint(ns: string, level: LogLevelName): boolean {
  if (levelValue(level) < levelValue(currentConfig.level)) return false;
  return namespaceEnabled(ns);
}

function formatPrefix(
  ns: string,
  level: LogLevelName
): [string, string] | string {
  const ts = new Date().toISOString().split("T")[1].replace("Z", "");
  if (!isBrowser) return `[${ts}] ${level.toUpperCase()} ${ns}:`;
  // Color palette per level
  const color =
    level === "debug"
      ? "color:#64748b" // slate-500
      : level === "info"
        ? "color:#2563eb" // blue-600
        : level === "warn"
          ? "color:#d97706" // amber-600
          : "color:#dc2626"; // red-600
  const text = `%c[${ts}] ${level.toUpperCase()} ${ns}:`;
  return [text, color];
}

export function createLogger(ns: string): Logger {
  const base =
    (level: LogLevelName, method: "debug" | "info" | "warn" | "error") =>
    (...args: unknown[]) => {
      if (!shouldPrint(ns, level)) return;
      const prefix = formatPrefix(ns, level);

      if (Array.isArray(prefix)) {
        (console as any)[method](prefix[0], prefix[1], ...args);
      } else {
        (console as any)[method](prefix, ...args);
      }
    };
  return {
    ns,
    setLevel(level: LogLevelName) {
      // For simplicity, we just raise global level if higher specificity needed
      if (levelValue(level) > levelValue(currentConfig.level)) {
        currentConfig.level = level;
        persistConfig();
      }
    },
    debug: base("debug", "debug"),
    info: base("info", "info"),
    warn: base("warn", "warn"),
    error: base("error", "error"),
  };
}

// Provide a default root logger
export const rootLogger = createLogger("app");

// Expose debug console helpers in dev for quick toggling
if (isBrowser && isDev) {
  // @ts-ignore
  window.__APP_LOGGER__ = {
    get config() {
      return getLoggerConfig();
    },
    setLevel: setGlobalLevel,
    enable: enableNamespaces,
    disable: disableNamespaces,
    clearFilters,
  };
}

export const logger = createLogger("app");
